import { useEffect, useRef, useState, type TouchEvent } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { LayoutGrid, X } from "lucide-react";
import { NAV_LINKS, MOBILE_TABS } from "./navLinks";

const tabs = NAV_LINKS.filter((link) => MOBILE_TABS.includes(link.to));

// How far the sheet has to be dragged down before letting go closes it.
const DISMISS_DRAG_PX = 80;

function matchesPath(pathname: string, to: string, end?: boolean): boolean {
  return end ? pathname === to : pathname === to || pathname.startsWith(`${to}/`);
}

/**
 * Mobile navigation: a bottom tab bar for the most-used pages, plus a More tab that slides
 * up a sheet listing every page. Hidden from md up, where the sidebar takes over.
 */
export function BottomNav() {
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  // Distance dragged down while a finger is on the sheet; null when no drag is happening,
  // which is also what turns the slide transition back on.
  const [dragY, setDragY] = useState<number | null>(null);
  const dragStartY = useRef(0);

  // Closes on any navigation, including the browser's back button.
  useEffect(() => {
    setOpen(false);
  }, [pathname]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  function onTouchStart(e: TouchEvent) {
    dragStartY.current = e.touches[0].clientY;
    setDragY(0);
  }
  function onTouchMove(e: TouchEvent) {
    setDragY(Math.max(0, e.touches[0].clientY - dragStartY.current));
  }
  function onTouchEnd() {
    if ((dragY ?? 0) > DISMISS_DRAG_PX) setOpen(false);
    setDragY(null);
  }

  const onTabPage = tabs.some((t) => matchesPath(pathname, t.to, t.end));
  const moreActive = open || !onTabPage;

  return (
    <>
      <nav
        aria-label="Main"
        className="fixed inset-x-0 bottom-0 z-30 border-t border-slate-800 bg-slate-950/95 pb-[env(safe-area-inset-bottom)] backdrop-blur md:hidden"
      >
        <div className="grid h-16 grid-cols-5">
          {tabs.map(({ to, label, icon: Icon, end }) => (
            <NavLink
              key={to}
              to={to}
              end={end}
              className={({ isActive }) =>
                `flex flex-col items-center justify-center gap-1 text-[11px] font-medium transition-colors ${
                  isActive && !open ? "text-emerald-400" : "text-slate-500 active:text-slate-300"
                }`
              }
            >
              <Icon className="h-5 w-5" />
              {label}
            </NavLink>
          ))}
          <button
            onClick={() => setOpen((v) => !v)}
            aria-expanded={open}
            aria-controls="more-sheet"
            className={`flex flex-col items-center justify-center gap-1 text-[11px] font-medium transition-colors ${
              moreActive ? "text-emerald-400" : "text-slate-500 active:text-slate-300"
            }`}
          >
            <LayoutGrid className="h-5 w-5" />
            More
          </button>
        </div>
      </nav>

      <div className={`fixed inset-0 z-40 md:hidden ${open ? "" : "pointer-events-none"}`} inert={!open}>
        <div
          className={`absolute inset-0 bg-black/60 transition-opacity duration-200 ${open ? "opacity-100" : "opacity-0"}`}
          onClick={() => setOpen(false)}
        />
        {/* touch-none: a downward drag here would otherwise trigger the browser's pull-to-refresh. */}
        <div
          id="more-sheet"
          role="dialog"
          aria-modal="true"
          aria-label="All pages"
          onTouchStart={onTouchStart}
          onTouchMove={onTouchMove}
          onTouchEnd={onTouchEnd}
          style={{ transform: open ? `translateY(${dragY ?? 0}px)` : "translateY(100%)" }}
          className={`absolute inset-x-0 bottom-0 touch-none rounded-t-2xl border-t border-slate-800 bg-slate-900 pb-[calc(1.25rem+env(safe-area-inset-bottom))] shadow-2xl ${
            dragY === null ? "transition-transform duration-200 ease-out" : ""
          }`}
        >
          <div className="flex justify-center pb-1 pt-3">
            <span className="h-1.5 w-10 rounded-full bg-slate-700" />
          </div>
          <div className="flex items-center justify-between px-5 pb-3">
            <h2 className="text-sm font-semibold text-slate-100">All pages</h2>
            <button
              onClick={() => setOpen(false)}
              className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-800 hover:text-slate-100"
              aria-label="Close"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
          <div className="grid grid-cols-3 gap-2 px-4">
            {NAV_LINKS.map(({ to, label, icon: Icon, end }) => (
              <NavLink
                key={to}
                to={to}
                end={end}
                // Tapping the page you're already on doesn't change the path, so the
                // navigation effect above wouldn't close the sheet by itself.
                onClick={() => setOpen(false)}
                className={({ isActive }) =>
                  `flex flex-col items-center gap-2 rounded-xl border px-2 py-4 text-xs font-medium transition-colors ${
                    isActive
                      ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-400"
                      : "border-slate-800 bg-slate-950/40 text-slate-300 active:bg-slate-800"
                  }`
                }
              >
                <Icon className="h-5 w-5" />
                {label}
              </NavLink>
            ))}
          </div>
        </div>
      </div>
    </>
  );
}
